<template>
	<div>
		<div v-for="prop in formattedPropsValues" :key="prop.ID" class="mb-2">
			<div class="fs-13 text-muted">{{ prop.NAME }}:</div>
			<ul class="sku-list">
				<li
					v-for="value in prop.values" :key="value.ID"
					class="sku-list__item"
					:class="{
						'sku-list__item--selected': product.tree['PROP_' + prop.ID] == value.ID,
						'sku-list__item--disabled': cantBuy(value.ID)
					}"
				>
					<div
						v-if="prop.SHOW_MODE == 'PICT'"
						@click.prevent="select(prop.ID, value.ID)"
						class="sku-list__value sku-list__value--image"
						:title="value.NAME"
					>
						<div class="sku-list__image" :style="{backgroundImage: 'url(' + (value.PICT.SRC || images.noPropValue) + ')'}"></div>
					</div>
					<div
						v-else
						@click.prevent="select(prop.ID, value.ID)"
						class="sku-list__value sku-list__value--text"
					>
						{{ value.NAME }}
					</div>
				</li>
			</ul>
		</div>

		<div class="mt-2" v-if="showModifications && formattedPropsValues.length">
			<a href="#" @click.prevent="row._IS_EXPAND = !row._IS_EXPAND" class="fs-13 text-nowrap">
				<template v-if="!row._IS_EXPAND">
					{{ messages.RS_B2B_CS_SHOW_MODIFICATIONS }}
					<svg class="icon-svg"><use :href="spritePath + '#angle-small-down'"></use></svg>
				</template>
				<template v-else>
					{{ messages.RS_B2B_CS_HIDE_MODIFICATIONS }}
					<svg class="icon-svg"><use :href="spritePath + '#angle-small-up'"></use></svg>
				</template>
			</a>
		</div>

	</div>
</template>

<script>
import findKey from 'lodash/findKey';
import findIndex from 'lodash/findIndex';
import isMatch from 'lodash/isMatch';
import reduce from 'lodash/reduce';
import map from 'lodash/map';
import flatMap from 'lodash/flatMap';
import FieldMixin from './FieldMixin';

export default {

	mixins: [FieldMixin],

	props: {
		showModifications: {
			type: Boolean,
			default: true
		}
	},

	computed: {

		skuProps()
		{
			return this.$store.getters[`${this.$root.$namespace}/getSkuPropsByIblockId`](this.row.iblockId);
		},

		treeProps()
		{
			return this.skuProps.filter(prop => this.propsValues[prop.ID]);
		},

		propsValues()
		{
			return this.row.sku;
		},

		disabledValues()
		{
			return flatMap(this.propsValues, (values, propId) => {
				return reduce(values, (result, value, valueId) => {
					if (!!!this.findProduct(propId, parseInt(valueId)))
						result.push(propId + '_' + valueId);

					return result;
				}, []);
			});
		},

		cantBuyValues()
		{
			return flatMap(this.treeProps, prop => {
				return Object.keys(this.propsValues[prop.ID])
					.filter(valueId => !this.isDisabled(prop.ID, valueId))
					.reduce((result, strValueId) => {
						const valueId = parseInt(strValueId);
						if (!this.findProduct(prop.ID, valueId, true))
							result.push(valueId);

						return result;
					}, []);
			});
		},

		formattedPropsValues()
		{
			return this.skuProps
				.filter(prop => this.propsValues[prop.ID])
				.map(prop => ({
					ID: prop.ID,
					NAME: prop.NAME,
					SHOW_MODE: prop.SHOW_MODE,
					values: Object.values(prop.VALUES)
						.filter(value => this.propsValues[prop.ID][value.ID] && !this.isDisabled(prop.ID, value.ID))
						.sort((v1, v2) => v1.SORT - v2.SORT)
				}));
		},

		images()
		{
			return ((this.$root.params || {}).imagesPath) || {};
		},

		messages()
		{
			return Object.freeze({
				'RS_B2B_CS_SHOW_MODIFICATIONS': BX.message('RS_B2B_CS_SHOW_MODIFICATIONS'),
				'RS_B2B_CS_HIDE_MODIFICATIONS': BX.message('RS_B2B_CS_HIDE_MODIFICATIONS')
			});
		},

		spritePath()
		{
			return B2BPortal.config.spritePath;
		},
	},

	methods: {

		isDisabled(propId, valueId)
		{
			return this.disabledValues.includes(propId + '_' + valueId);
		},

		cantBuy(valueId)
		{
			return this.cantBuyValues.includes(valueId);
		},

		select(propId, valueId)
		{
			if (!this.isDisabled(propId, valueId))
			{
				this.row.selected = this.findNearestProduct(propId, valueId, !this.cantBuy(valueId)) || this.row.selected;
			}
		},

		getFilter(propId, valueId, index = 0)
		{
			const filter = {};

			if (index > this.treeProps.length)
			{
				index = this.treeProps.length;
			}

			for (let i = 0, strPropName = ''; i < index; i++)
			{
				strPropName = 'PROP_' + this.treeProps[i].ID;
				filter[strPropName] = this.product.tree[strPropName];
			}

			filter['PROP_' + propId] = valueId;

			return filter;
		},

		findProductByFilter(filter, canBuy = false)
		{
			if (canBuy)
			{
				return findKey(this.row.products, product => isMatch(product.tree, filter) && product.canBuy);
			}
			else
			{
				return findKey(this.row.products, product => isMatch(product.tree, filter));
			}
		},

		findNearestProduct(propId, valueId, canBuy = false)
		{
			let productId = false;

			for (let i = this.treeProps.length, filter; i >= 0; i--)
			{
				productId = this.findProductByFilter(this.getFilter(propId, valueId, i), canBuy);

				if (productId)
				{
					break;
				}
			}

			return productId;
		},

		findProduct(propId, valueId, canBuy = false)
		{
			const index = findIndex(this.skuProps, prop => prop.ID == propId);
			return -1 < index ? this.findProductByFilter(this.getFilter(propId, valueId, index), canBuy) : false;
		},
	}

}
</script>

<style lang="scss" scoped>
@import '../../../../../../resources/styles/abstracts/index';

.sku-list {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    margin: 0;
    padding: 0;

	&__item {
		display: block;
		padding: 0;
		margin: 3px;
	}

	&__value {
		border: 1px solid transparent;
		border-radius: .5rem;
		display: block;
		cursor: pointer;
		height: 1.875rem;
	}

	&__value--image {
		padding: .1875rem;
		position: relative;
		width: 1.875rem;
	}

	&__image {
		background-size: cover;
		background-position: center;
		border-radius: .375rem;
		height: 1.375rem;
		overflow: hidden;
		width: 1.375rem;
	}

	&__value--text {
		border-color: $body-bg;
		font-size: .75rem;
		min-width: 1.875rem;
		color: #1D1D1F;
		padding: .3125rem;
		text-align: center;
	}

	&__item--disabled > &__value {
		opacity: 0.65;
	}

	&:hover > &__value--text,
	&__item--selected > &__value--text {
		// background-color: var(--primary, #{$primary});
		border-color: var(--primary, #{$primary});
		color: var(--primary, #{$primary});
	}

	&:hover > &__value--image,
	&__item--selected > &__value--image {
		border-color: var(--primary, #{$primary});
	}
}
</style>